import {Presenter} from '../support/with_presenter';
import {DragContext, Draggables} from '../../business/drag_context';
import {SprintUpdater} from '../../business/sprint_updater';
import {EstimateUpdater} from '../../business/estimate_updater';
import {SprintSelectionManager} from '../../business/sprint_selection_manager';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {UsersRepository} from '../../persistence/users_repository';
import {SprintsRepository} from '../../persistence/sprints_repository';
import {Sprint} from '../../models/sprint';
import {User} from '../../models/user';
import {HighlightState, SprintHighlighter} from '../../business/sprint_highlighter';
import {CompositeSubscription} from '../../support/composite_subscription';

export class SprintBlockPresenter implements Presenter {
    public saving: boolean = false;
    public hours: number;
    public isExtra: boolean;
    public modalVisible = false;
    public estimateState: string;
    public selected: boolean = false;
    public lastEditBy: User | undefined;
    public hightlightState: HighlightState = HighlightState.Idle;

    private subscriptions = new CompositeSubscription();

    constructor(
        private sprintId: number,
        private estimateId: number,
        private initialHours: number,
        private initialIsExtra: boolean,
        private initialEstimateState: string,
        private dragContext: DragContext,
        private sprintUpdater: SprintUpdater,
        private estimateUpdater: EstimateUpdater,
        private sprintSelectionManager: SprintSelectionManager,
        private userRepository: UsersRepository,
        private sprintRepository: SprintsRepository,
        private sprintHighlighter: SprintHighlighter,
    ) {
        this.hours = initialHours;
        this.isExtra = initialIsExtra;
        this.estimateState = initialEstimateState;
    }

    public mount(): void {
        this.subscriptions.add(
            this.sprintSelectionManager.selection
                .pipe(
                    map((sprintIds) => sprintIds.indexOf(this.sprintId) !== -1),
                    distinctUntilChanged(),
                )
                .subscribe((selected) => this.updateSelectedStatus(selected)),
        );

        this.subscriptions.add(
            this.sprintRepository
                .streamById(this.sprintId)
                .pipe(
                    filter((sprint): sprint is Sprint => sprint !== null),
                    map((sprint) => this.userRepository.findById(sprint.last_edit_by)),
                )
                .subscribe((lastEditBy) => (this.lastEditBy = lastEditBy)),
        );

        this.subscriptions.add(
            this.sprintHighlighter
                .isHighlighted(this.sprintId)
                .subscribe((hightlightState) => (this.hightlightState = hightlightState)),
        );
    }

    public unmount(): void {
        this.subscriptions.clear();
    }

    private updateSelectedStatus(selected: boolean) {
        this.selected = selected;
    }

    public handleDragStart() {
        this.dragContext.register({
            type: Draggables.Sprint,
            id: this.sprintId,
        });
    }

    public handleDragEnd() {
        this.dragContext.clear();
    }

    public handleClick(ctrlKeyDown: boolean, shiftKeyDown: boolean) {
        if (shiftKeyDown) {
            this.sprintSelectionManager.selectRangeUntil(this.sprintId);
        } else if (ctrlKeyDown) {
            this.sprintSelectionManager.select(this.sprintId);
        } else {
            this.modalVisible = true;
        }
    }

    public onRequestModalClose() {
        this.modalVisible = false;
    }

    public onHoursChange(hours: number) {
        this.hours = hours;
    }

    public onIsExtraChange(isExtra: boolean) {
        this.isExtra = isExtra;
    }

    public onEstimateStateChange(estimateState: string) {
        this.estimateState = estimateState;
    }

    public async onSaveClick() {
        try {
            this.saving = true;
            if (this.hours !== this.initialHours || this.isExtra !== this.initialIsExtra) {
                await this.sprintUpdater.update(this.sprintId, this.hours, this.isExtra);
                this.initialHours = this.hours;
                this.initialIsExtra = this.isExtra;
            }
            if (this.estimateState !== this.initialEstimateState) {
                await this.estimateUpdater.update(this.estimateId, this.estimateState);
                this.initialEstimateState = this.estimateState;
            }
        } finally {
            this.saving = false;
        }
        this.modalVisible = false;
    }

    public onSubmit() {
        this.onSaveClick();
    }

    public onMouseEnter() {
        this.sprintHighlighter.register(this.sprintId);
    }

    public onMouseLeave() {
        this.sprintHighlighter.clear(this.sprintId);
    }
}
